import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { firebase } from "../../firebase/config";
import "./NewsletterLeadsAdmin.css";

// ONE list of every person Bridge Champions knows about, replacing the separate
// "Signups" and "Newsletter leads" pages. Someone who typed their email into an
// article AND later created an account was previously two rows on two screens;
// here they are one row, merged on the email address.
//
// Sources:
//   members         - anyone with an account (name, subscription status)
//   newsletterLeads - anyone who submitted the article capture (which article)
//
// Same admin gate as the pages it replaces: state.auth.a === true.

const toDate = (ts) => (ts && ts.toDate ? ts.toDate() : ts ? new Date(ts) : null);
const formatDateTime = (ts) => {
  const d = toDate(ts);
  return d ? d.toLocaleString() : "";
};
const relative = (ts) => {
  const d = toDate(ts);
  if (!d) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days < 31) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
};

const leadName = (l) =>
  [l.firstName, l.surname].filter(Boolean).join(" ").trim() || (l.name || "").trim();
const firstNameOf = (name) => String(name || "").trim().split(/\s+/)[0] || "";
const emailKey = (e) => String(e || "").trim().toLowerCase();

const SOURCE_LABEL = {
  "article-top-capture": "Inline (top)",
  "article-footer-capture": "Inline (footer)",
  "article-popup-capture": "Popup",
};
const sourceLabel = (s) => SOURCE_LABEL[s] || s || "";

// One badge per person, in the order that matters when scanning the list.
const statusOf = (p) => {
  if (!p.hasAccount) return { label: "Lead", cls: "lead" };
  const exp = toDate(p.subscriptionExpires);
  const live = p.subscriptionActive === true && exp && exp.getTime() > Date.now();
  if (!live) return { label: "Account", cls: "none" };
  if (p.stripeStatus === "trialing") return { label: "Trial", cls: "trial" };
  return { label: p.tier === "premium" ? "Premium" : "Basic", cls: "paid" };
};

const FILTERS = [
  { key: "all", label: "Everyone" },
  { key: "subscribers", label: "Subscribers" },
  { key: "accounts", label: "Accounts, no sub" },
  { key: "leads", label: "Leads (no account)" },
];

export default function PeopleAdmin(props) {
  const { history } = props;
  const isAdmin = useSelector((state) => state.auth.a === true);
  const adminChecked = useSelector((state) => state.auth.a !== undefined);
  const [members, setMembers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loadedMembers, setLoadedMembers] = useState(false);
  const [loadedLeads, setLoadedLeads] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    if (!isAdmin) return undefined;
    const db = firebase.firestore();
    const unsubMembers = db
      .collection("members")
      .orderBy("createdAt", "desc")
      .limit(1000)
      .onSnapshot(
        (snap) => {
          const next = [];
          snap.forEach((doc) => next.push({ id: doc.id, ...doc.data() }));
          setMembers(next);
          setLoadedMembers(true);
        },
        (err) => {
          console.error("PeopleAdmin members error:", err);
          setError((err && err.message) || "Failed to load accounts.");
          setLoadedMembers(true);
        }
      );
    const unsubLeads = db
      .collection("newsletterLeads")
      .orderBy("createdAt", "desc")
      .limit(1000)
      .onSnapshot(
        (snap) => {
          const next = [];
          snap.forEach((doc) => next.push({ id: doc.id, ...doc.data() }));
          setLeads(next);
          setLoadedLeads(true);
        },
        (err) => {
          console.error("PeopleAdmin leads error:", err);
          setError((err && err.message) || "Failed to load leads.");
          setLoadedLeads(true);
        }
      );
    return () => {
      unsubMembers();
      unsubLeads();
    };
  }, [isAdmin]);

  const loading = !loadedMembers || !loadedLeads;

  // Merge both collections into one row per email address.
  const people = useMemo(() => {
    const by = new Map();
    members.forEach((m) => {
      const k = emailKey(m.email);
      if (!k) return;
      by.set(k, {
        key: k,
        email: m.email,
        name: (m.displayName || "").trim(),
        hasAccount: true,
        tier: m.tier,
        stripeStatus: m.stripeStatus,
        subscriptionActive: m.subscriptionActive,
        subscriptionExpires: m.subscriptionExpires,
        joinedAt: m.createdAt,
        capturedAt: null,
        captures: [],
      });
    });
    leads.forEach((l) => {
      const k = emailKey(l.email);
      if (!k) return;
      const capture = {
        at: l.createdAt,
        source: sourceLabel(l.source),
        articleTitle: l.articleTitle || "",
        sourcePath: l.sourcePath || "",
      };
      const existing = by.get(k);
      if (existing) {
        existing.captures.push(capture);
        // Earliest capture is when we first heard from them.
        const d = toDate(l.createdAt);
        const cur = toDate(existing.capturedAt);
        if (d && (!cur || d.getTime() < cur.getTime())) existing.capturedAt = l.createdAt;
        if (!existing.name && leadName(l)) existing.name = leadName(l);
        return;
      }
      by.set(k, {
        key: k,
        email: l.email,
        name: leadName(l),
        hasAccount: false,
        joinedAt: null,
        capturedAt: l.createdAt,
        captures: [capture],
      });
    });
    // Newest first, on whichever date we have for them.
    const when = (p) => {
      const a = toDate(p.joinedAt);
      const b = toDate(p.capturedAt);
      return Math.max(a ? a.getTime() : 0, b ? b.getTime() : 0);
    };
    return [...by.values()].sort((x, y) => when(y) - when(x));
  }, [members, leads]);

  const stats = useMemo(() => {
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const seen = (p, since) => {
      const a = toDate(p.joinedAt);
      const b = toDate(p.capturedAt);
      const t = Math.max(a ? a.getTime() : 0, b ? b.getTime() : 0);
      return t >= since;
    };
    return {
      total: people.length,
      day: people.filter((p) => seen(p, dayAgo)).length,
      week: people.filter((p) => seen(p, weekAgo)).length,
      subscribers: people.filter((p) => ["paid", "trial"].includes(statusOf(p).cls)).length,
      leadsOnly: people.filter((p) => !p.hasAccount).length,
      both: people.filter((p) => p.hasAccount && p.captures.length).length,
    };
  }, [people]);

  // Which articles actually bring people in - worth keeping from the old page.
  const topArticles = useMemo(() => {
    const by = {};
    leads.forEach((l) => {
      const k = l.articleTitle || l.articleId || "(unknown)";
      if (!by[k]) by[k] = { n: 0, path: l.sourcePath || "" };
      by[k].n += 1;
    });
    return Object.entries(by).sort((a, b) => b[1].n - a[1].n).slice(0, 6);
  }, [leads]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return people.filter((p) => {
      const cls = statusOf(p).cls;
      if (filter === "subscribers" && !["paid", "trial"].includes(cls)) return false;
      if (filter === "accounts" && cls !== "none") return false;
      if (filter === "leads" && p.hasAccount) return false;
      if (!needle) return true;
      const hay = [p.name, p.email, statusOf(p).label].concat(
        p.captures.map((c) => c.articleTitle)
      );
      return hay.filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [people, q, filter]);

  const copy = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1600);
    } catch (e) {
      setError("Could not copy to clipboard.");
    }
  };

  useEffect(() => {
    if (adminChecked && !isAdmin) history.push("/");
  }, [adminChecked, isAdmin, history]);
  if (!isAdmin) return null;

  const emails = filtered.map((p) => p.email).filter(Boolean);

  return (
    <div className="NLAdmin">
      <Helmet>
        <title>People &mdash; Bridge Champions</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="NLAdmin-head">
        <h1 className="NLAdmin-title">People</h1>
        <p className="NLAdmin-sub">
          Everyone on record &mdash; account holders and article email captures in one list,
          newest first. Someone who did both appears once.
        </p>
      </div>

      <div className="NLAdmin-stats">
        <div className="NLAdmin-stat"><b>{stats.total}</b><span>people</span></div>
        <div className="NLAdmin-stat"><b>{stats.day}</b><span>last 24 hrs</span></div>
        <div className="NLAdmin-stat"><b>{stats.week}</b><span>last 7 days</span></div>
        <div className="NLAdmin-stat"><b>{stats.subscribers}</b><span>subscribers</span></div>
        <div className="NLAdmin-stat"><b>{stats.leadsOnly}</b><span>leads, no account</span></div>
      </div>

      {stats.both ? (
        <div className="NLAdmin-chips">
          <span className="NLAdmin-chip">
            Gave their email <b>and</b> later created an account: <b>{stats.both}</b>
          </span>
        </div>
      ) : null}

      {topArticles.length ? (
        <div className="NLAdmin-panel">
          <div className="NLAdmin-panelTitle">Articles bringing in the most email captures</div>
          <ol className="NLAdmin-topList">
            {topArticles.map((entry) => (
              <li key={entry[0]}>
                <span className="NLAdmin-topN">{entry[1].n}</span>
                {entry[1].path ? (
                  <a href={entry[1].path} target="_blank" rel="noopener noreferrer">{entry[0]}</a>
                ) : (
                  entry[0]
                )}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div className="NLAdmin-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`NLAdmin-chip${filter === f.key ? " NLAdmin-chip--on" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="NLAdmin-toolbar">
        <input
          className="NLAdmin-search"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, status or article&hellip;"
          aria-label="Search people"
        />
        <button
          className="NLAdmin-btn"
          onClick={() => copy(emails.join(", "), "commas")}
          disabled={!emails.length}
        >
          Copy {emails.length} email{emails.length === 1 ? "" : "s"}
        </button>
        <button
          className="NLAdmin-btn"
          onClick={() => copy(emails.join("\n"), "lines")}
          disabled={!emails.length}
        >
          Copy one per line
        </button>
        {copied ? <span className="NLAdmin-copied">Copied &#10003;</span> : null}
      </div>

      {error ? <div className="NLAdmin-error">{error}</div> : null}
      {loading ? <div className="NLAdmin-empty">Loading&hellip;</div> : null}
      {!loading && !filtered.length ? (
        <div className="NLAdmin-empty">{people.length ? "Nothing matches that." : "Nobody yet."}</div>
      ) : null}

      {!loading && filtered.length ? (
        <div className="NLAdmin-tableWrap">
          <table className="NLAdmin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>How we met</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const st = statusOf(p);
                const first = p.captures[0];
                return (
                  <tr key={p.key}>
                    <td>{p.name || <span className="NLAdmin-dim">&mdash;</span>}</td>
                    <td>
                      <a href={`mailto:${p.email}`} className="NLAdmin-email">{p.email}</a>
                    </td>
                    <td><span className={`NLAdmin-tag NLAdmin-tag--${st.cls}`}>{st.label}</span></td>
                    <td className="NLAdmin-article">
                      {first ? (
                        <>
                          <span className="NLAdmin-src">{first.source}</span>{" "}
                          {first.sourcePath ? (
                            <a href={first.sourcePath} target="_blank" rel="noopener noreferrer">
                              {first.articleTitle || first.sourcePath}
                            </a>
                          ) : (
                            first.articleTitle
                          )}
                        </>
                      ) : (
                        <span className="NLAdmin-src">Signed up</span>
                      )}
                    </td>
                    <td className="NLAdmin-when" title={formatDateTime(p.joinedAt || p.capturedAt)}>
                      {relative(p.joinedAt || p.capturedAt)}
                    </td>
                    <td>
                      <button
                        className="NLAdmin-btn NLAdmin-btn--sm"
                        onClick={() =>
                          copy(
                            firstNameOf(p.name) ? `${firstNameOf(p.name)} <${p.email}>` : p.email,
                            p.email
                          )
                        }
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
